import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, Filter, Users, Star, ArrowRight, Code2, Target, Zap, Shield, Smartphone, Globe, PlayCircle, BookOpen, TrendingUp, User, LogOut } from 'lucide-react';
import Button from '../../components/ui/Button';
import RoadmapCard from './components/RoadmapCard';
import FilterSidebar from './components/FilterSidebar';
import RecommendationEngine from './components/RecommendationEngine';
import ComparisonTool from './components/ComparisonTool';

const TestingRoadmapsHub = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilters, setSelectedFilters] = useState({
    level: 'all',
    technology: 'all',
    industry: 'all',
    timeCommitment: 'all'
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedRoadmaps, setSelectedRoadmaps] = useState([]);
  const [showComparison, setShowComparison] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  // Mock user authentication check
  useEffect(() => {
    const storedUser = localStorage.getItem('currentUser');
    if (storedUser) {
      try {
        setCurrentUser(JSON.parse(storedUser));
      } catch (error) {
        console.error('Error parsing stored user:', error);
      }
    }
  }, []);

  // Comprehensive roadmaps data with all 20 roadmaps
  const roadmaps = [
    {
      id: 1,
      slug: 'manual-testing',
      title: 'Manual Testing Fundamentals',
      description: 'Master the art of manual testing with comprehensive test case design, execution strategies, and defect management.',
      difficulty: 'Beginner',
      estimatedTime: '6-8 weeks',
      prerequisites: ['Basic computer skills', 'Understanding of software'],
      technology: 'General',
      industry: 'All Industries',
      icon: <Target className="w-8 h-8" />,
      color: 'bg-green-500',
      enrolled: 1250,
      rating: 4.8,
      completionRate: 92,
      outcomes: ['Test Case Design', 'Bug Reporting', 'Test Planning'],
      featured: true,
      trending: false,
      missions: 15,
      certifications: ['ISTQB Foundation', 'Manual Testing Pro']
    },
    {
      id: 2,
      slug: 'automation-testing-java',
      title: 'Automation Testing with Java',
      description: 'Build robust automation frameworks using Java, Selenium WebDriver, TestNG, and Maven for enterprise applications.',
      difficulty: 'Intermediate',
      estimatedTime: '12-14 weeks',
      prerequisites: ['Java basics', 'OOP concepts', 'Manual testing experience'],
      technology: 'Java',
      industry: 'Enterprise',
      icon: <Code2 className="w-8 h-8" />,
      color: 'bg-orange-500',
      enrolled: 850,
      rating: 4.7,
      completionRate: 78,
      outcomes: ['Selenium WebDriver', 'TestNG Framework', 'Page Object Model'],
      featured: true,
      trending: true,
      missions: 25,
      certifications: ['Java Test Automation Expert']
    },
    {
      id: 3,
      slug: 'automation-testing-python',
      title: 'Automation Testing with Python',
      description: 'Learn Python-based automation testing with Selenium, pytest, and modern testing frameworks for rapid development.',
      difficulty: 'Intermediate',
      estimatedTime: '10-12 weeks',
      prerequisites: ['Python basics', 'Basic programming', 'Manual testing'],
      technology: 'Python',
      industry: 'All Industries',
      icon: <Code2 className="w-8 h-8" />,
      color: 'bg-blue-500',
      enrolled: 1100,
      rating: 4.9,
      completionRate: 85,
      outcomes: ['Selenium Python', 'Pytest Framework', 'API Testing'],
      featured: true,
      trending: true,
      missions: 22,
      certifications: ['Python Test Automation Specialist']
    },
    {
      id: 4,
      slug: 'automation-testing-javascript',
      title: 'JavaScript Test Automation',
      description: 'Master modern JavaScript testing with Cypress, Jest, and WebDriverIO for web application automation.',
      difficulty: 'Intermediate',
      estimatedTime: '10-12 weeks',
      prerequisites: ['JavaScript fundamentals', 'Web development basics'],
      technology: 'JavaScript',
      industry: 'Web Development',
      icon: <Code2 className="w-8 h-8" />,
      color: 'bg-yellow-500',
      enrolled: 750,
      rating: 4.6,
      completionRate: 80,
      outcomes: ['Cypress Testing', 'Jest Framework', 'WebDriverIO'],
      featured: false,
      trending: true,
      missions: 20,
      certifications: ['JavaScript Test Expert']
    },
    {
      id: 5,
      slug: 'api-testing',
      title: 'API Testing Mastery',
      description: 'Comprehensive API testing with REST, GraphQL, Postman, RestAssured, and automated API validation.',
      difficulty: 'Intermediate',
      estimatedTime: '8-10 weeks',
      prerequisites: ['HTTP/REST basics', 'JSON understanding'],
      technology: 'API',
      industry: 'All Industries',
      icon: <Globe className="w-8 h-8" />,
      color: 'bg-purple-500',
      enrolled: 920,
      rating: 4.8,
      completionRate: 87,
      outcomes: ['REST API Testing', 'GraphQL Testing', 'API Automation'],
      featured: true,
      trending: false,
      missions: 18,
      certifications: ['API Testing Professional']
    },
    {
      id: 6,
      slug: 'bdd-cucumber',
      title: 'BDD with Cucumber',
      description: 'Behavior-Driven Development using Cucumber, Gherkin syntax, and collaborative testing approaches.',
      difficulty: 'Intermediate',
      estimatedTime: '6-8 weeks',
      prerequisites: ['Basic programming', 'Agile methodology'],
      technology: 'BDD',
      industry: 'Agile Teams',
      icon: <BookOpen className="w-8 h-8" />,
      color: 'bg-green-600',
      enrolled: 680,
      rating: 4.5,
      completionRate: 82,
      outcomes: ['Gherkin Writing', 'Step Definitions', 'BDD Framework'],
      featured: false,
      trending: false,
      missions: 16,
      certifications: ['BDD Practitioner']
    },
    {
      id: 7,
      slug: 'playwright-testing',
      title: 'Modern Testing with Playwright',
      description: 'Next-generation browser automation with Playwright for cross-browser testing and modern web apps.',
      difficulty: 'Intermediate',
      estimatedTime: '8-10 weeks',
      prerequisites: ['JavaScript/TypeScript', 'Web application basics'],
      technology: 'Playwright',
      industry: 'Modern Web',
      icon: <PlayCircle className="w-8 h-8" />,
      color: 'bg-indigo-500',
      enrolled: 540,
      rating: 4.7,
      completionRate: 83,
      outcomes: ['Cross-browser Testing', 'Auto-wait', 'Network Interception'],
      featured: false,
      trending: true,
      missions: 19,
      certifications: ['Playwright Expert']
    },
    {
      id: 8,
      slug: 'selenium-java',
      title: 'Selenium WebDriver with Java',
      description: 'Deep dive into Selenium WebDriver using Java with advanced patterns, frameworks, and best practices.',
      difficulty: 'Intermediate',
      estimatedTime: '12-14 weeks',
      prerequisites: ['Java proficiency', 'Selenium basics'],
      technology: 'Selenium',
      industry: 'Enterprise',
      icon: <Zap className="w-8 h-8" />,
      color: 'bg-red-500',
      enrolled: 1050,
      rating: 4.6,
      completionRate: 79,
      outcomes: ['Advanced Selenium', 'Framework Design', 'CI/CD Integration'],
      featured: false,
      trending: false,
      missions: 28,
      certifications: ['Selenium Java Master']
    },
    {
      id: 9,
      slug: 'selenium-python',
      title: 'Selenium WebDriver with Python',
      description: 'Python-powered Selenium automation with pytest, advanced locators, and scalable test architectures.',
      difficulty: 'Intermediate',
      estimatedTime: '10-12 weeks',
      prerequisites: ['Python skills', 'Selenium fundamentals'],
      technology: 'Selenium',
      industry: 'All Industries',
      icon: <Zap className="w-8 h-8" />,
      color: 'bg-blue-600',
      enrolled: 890,
      rating: 4.8,
      completionRate: 84,
      outcomes: ['Python Selenium Mastery', 'Pytest Advanced', 'Test Reporting'],
      featured: false,
      trending: false,
      missions: 24,
      certifications: ['Selenium Python Expert']
    },
    {
      id: 10,
      slug: 'selenium-javascript',
      title: 'Selenium with JavaScript/Node.js',
      description: 'JavaScript-based Selenium automation with Node.js, Mocha, and modern async/await patterns.',
      difficulty: 'Intermediate',
      estimatedTime: '10-12 weeks',
      prerequisites: ['JavaScript proficiency', 'Node.js basics'],
      technology: 'Selenium',
      industry: 'Web Development',
      icon: <Zap className="w-8 h-8" />,
      color: 'bg-yellow-600',
      enrolled: 620,
      rating: 4.5,
      completionRate: 77,
      outcomes: ['Node.js Selenium', 'Mocha Framework', 'Async Testing'],
      featured: false,
      trending: false,
      missions: 21,
      certifications: ['Selenium JavaScript Pro']
    },
    {
      id: 11,
      slug: 'performance-testing',
      title: 'Performance Testing Excellence',
      description: 'Master performance testing with JMeter, load testing strategies, and performance optimization techniques.',
      difficulty: 'Advanced',
      estimatedTime: '10-12 weeks',
      prerequisites: ['Testing experience', 'System architecture basics'],
      technology: 'Performance',
      industry: 'All Industries',
      icon: <TrendingUp className="w-8 h-8" />,
      color: 'bg-pink-500',
      enrolled: 450,
      rating: 4.7,
      completionRate: 75,
      outcomes: ['JMeter Mastery', 'Load Testing', 'Performance Analysis'],
      featured: true,
      trending: false,
      missions: 17,
      certifications: ['Performance Testing Expert']
    },
    {
      id: 12,
      slug: 'security-testing-basics',
      title: 'Security Testing Fundamentals',
      description: 'Essential security testing concepts, OWASP guidelines, and penetration testing basics for testers.',
      difficulty: 'Advanced',
      estimatedTime: '8-10 weeks',
      prerequisites: ['Testing experience', 'Basic security concepts'],
      technology: 'Security',
      industry: 'All Industries',
      icon: <Shield className="w-8 h-8" />,
      color: 'bg-gray-700',
      enrolled: 380,
      rating: 4.6,
      completionRate: 72,
      outcomes: ['OWASP Testing', 'Vulnerability Assessment', 'Security Tools'],
      featured: false,
      trending: true,
      missions: 15,
      certifications: ['Security Testing Specialist']
    },
    {
      id: 13,
      slug: 'mobile-testing-appium',
      title: 'Mobile Testing with Appium',
      description: 'Comprehensive mobile app testing for iOS and Android using Appium, device farms, and mobile-specific strategies.',
      difficulty: 'Advanced',
      estimatedTime: '12-14 weeks',
      prerequisites: ['Mobile development basics', 'Automation experience'],
      technology: 'Mobile',
      industry: 'Mobile Apps',
      icon: <Smartphone className="w-8 h-8" />,
      color: 'bg-teal-500',
      enrolled: 320,
      rating: 4.4,
      completionRate: 68,
      outcomes: ['iOS Testing', 'Android Testing', 'Device Farm Integration'],
      featured: false,
      trending: true,
      missions: 23,
      certifications: ['Mobile Testing Expert']
    },
    {
      id: 14,
      slug: 'ci-cd-for-testing',
      title: 'CI/CD Pipeline Testing',
      description: 'Integration of automated testing in CI/CD pipelines using Jenkins, GitLab CI, and containerized testing.',
      difficulty: 'Advanced',
      estimatedTime: '8-10 weeks',
      prerequisites: ['DevOps basics', 'Automation experience'],
      technology: 'DevOps',
      industry: 'All Industries',
      icon: <Zap className="w-8 h-8" />,
      color: 'bg-cyan-500',
      enrolled: 410,
      rating: 4.5,
      completionRate: 73,
      outcomes: ['Pipeline Integration', 'Docker Testing', 'Continuous Testing'],
      featured: false,
      trending: true,
      missions: 16,
      certifications: ['DevOps Testing Specialist']
    },
    {
      id: 15,
      slug: 'esb-odm-testing',
      title: 'ESB & ODM Testing',
      description: 'Enterprise Service Bus and Operational Data Management testing for complex enterprise architectures.',
      difficulty: 'Expert',
      estimatedTime: '14-16 weeks',
      prerequisites: ['Enterprise architecture', 'Advanced testing'],
      technology: 'Enterprise',
      industry: 'Enterprise',
      icon: <Globe className="w-8 h-8" />,
      color: 'bg-indigo-700',
      enrolled: 180,
      rating: 4.3,
      completionRate: 65,
      outcomes: ['ESB Testing', 'ODM Validation', 'Enterprise Integration'],
      featured: false,
      trending: false,
      missions: 20,
      certifications: ['Enterprise Testing Expert']
    },
    {
      id: 16,
      slug: 'test-design-and-strategy',
      title: 'Test Design & Strategy',
      description: 'Advanced test design techniques, risk-based testing, and strategic test planning methodologies.',
      difficulty: 'Expert',
      estimatedTime: '10-12 weeks',
      prerequisites: ['Extensive testing experience', 'Leadership skills'],
      technology: 'Strategy',
      industry: 'All Industries',
      icon: <Target className="w-8 h-8" />,
      color: 'bg-purple-700',
      enrolled: 220,
      rating: 4.8,
      completionRate: 78,
      outcomes: ['Test Strategy', 'Risk Assessment', 'Test Leadership'],
      featured: true,
      trending: false,
      missions: 14,
      certifications: ['Test Strategy Expert']
    },
    {
      id: 17,
      slug: 'data-driven-testing',
      title: 'Data-Driven Testing',
      description: 'Master data-driven testing approaches, test data management, and parameterized testing strategies.',
      difficulty: 'Intermediate',
      estimatedTime: '6-8 weeks',
      prerequisites: ['Automation experience', 'Database basics'],
      technology: 'Data',
      industry: 'All Industries',
      icon: <BookOpen className="w-8 h-8" />,
      color: 'bg-emerald-500',
      enrolled: 340,
      rating: 4.6,
      completionRate: 81,
      outcomes: ['Test Data Management', 'Parameterization', 'Data Validation'],
      featured: false,
      trending: false,
      missions: 13,
      certifications: ['Data Testing Specialist']
    },
    {
      id: 18,
      slug: 'test-automation-architecture',
      title: 'Test Automation Architecture',
      description: 'Design scalable, maintainable test automation architectures and frameworks for enterprise applications.',
      difficulty: 'Expert',
      estimatedTime: '12-14 weeks',
      prerequisites: ['Senior automation experience', 'Architecture knowledge'],
      technology: 'Architecture',
      industry: 'Enterprise',
      icon: <Code2 className="w-8 h-8" />,
      color: 'bg-slate-700',
      enrolled: 160,
      rating: 4.7,
      completionRate: 70,
      outcomes: ['Framework Architecture', 'Scalable Design', 'Technical Leadership'],
      featured: false,
      trending: false,
      missions: 18,
      certifications: ['Automation Architect']
    },
    {
      id: 19,
      slug: 'accessibility-testing',
      title: 'Accessibility Testing',
      description: 'Comprehensive accessibility testing using WCAG guidelines, screen readers, and automated accessibility tools.',
      difficulty: 'Intermediate',
      estimatedTime: '6-8 weeks',
      prerequisites: ['Web testing experience', 'Accessibility awareness'],
      technology: 'Accessibility',
      industry: 'Web/Mobile',
      icon: <Users className="w-8 h-8" />,
      color: 'bg-rose-500',
      enrolled: 280,
      rating: 4.5,
      completionRate: 76,
      outcomes: ['WCAG Compliance', 'Screen Reader Testing', 'Accessibility Tools'],
      featured: false,
      trending: true,
      missions: 12,
      certifications: ['Accessibility Testing Pro']
    },
    {
      id: 20,
      slug: 'exploratory-testing',
      title: 'Exploratory Testing Mastery',
      description: 'Advanced exploratory testing techniques, session-based testing, and creative testing approaches.',
      difficulty: 'Intermediate',
      estimatedTime: '6-8 weeks',
      prerequisites: ['Manual testing experience', 'Critical thinking'],
      technology: 'Exploratory',
      industry: 'All Industries',
      icon: <Search className="w-8 h-8" />,
      color: 'bg-amber-600',
      enrolled: 390,
      rating: 4.6,
      completionRate: 83,
      outcomes: ['Session-based Testing', 'Creative Testing', 'Bug Hunting'],
      featured: false,
      trending: false,
      missions: 11,
      certifications: ['Exploratory Testing Expert']
    }
  ];

  // Filter roadmaps based on search and filters
  const filteredRoadmaps = roadmaps?.filter((roadmap) => {
    const matchesSearch = roadmap?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         roadmap?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    
    const matchesLevel = selectedFilters?.level === 'all' || roadmap?.difficulty === selectedFilters?.level;
    const matchesTechnology = selectedFilters?.technology === 'all' || roadmap?.technology === selectedFilters?.technology;
    const matchesIndustry = selectedFilters?.industry === 'all' || roadmap?.industry === selectedFilters?.industry;
    
    return matchesSearch && matchesLevel && matchesTechnology && matchesIndustry;
  });

  // Get featured and trending roadmaps
  const featuredRoadmaps = roadmaps?.filter(roadmap => roadmap?.featured);
  const trendingRoadmaps = roadmaps?.filter(roadmap => roadmap?.trending);

  const handleRoadmapSelect = (roadmap) => {
    if (selectedRoadmaps?.some(r => r?.id === roadmap?.id)) {
      setSelectedRoadmaps(selectedRoadmaps?.filter(r => r?.id !== roadmap?.id));
    } else if (selectedRoadmaps?.length < 3) {
      setSelectedRoadmaps([...selectedRoadmaps, roadmap]);
    }
  };

  const handleEnrollment = (roadmapSlug) => {
    // Navigate to roadmap details or enrollment
    navigate(`/roadmap/${roadmapSlug}`);
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/90 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                <Code2 className="w-6 h-6 text-white" />
              </div>
              <Link to="/" className="text-xl font-bold hover:text-blue-500 transition-colors">
                Testing University
              </Link>
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              <Link to="/" className="text-muted-foreground hover:text-foreground transition-colors">Home</Link>
              <Link to="/testing-roadmaps-hub" className="text-foreground font-medium">Roadmaps</Link>
              <Link to="/tutorial-learning-center" className="text-muted-foreground hover:text-foreground transition-colors">Tutorials</Link>
              <Link to="/challenges" className="text-muted-foreground hover:text-foreground transition-colors">Challenges</Link>
              <Link to="/interview-prep" className="text-muted-foreground hover:text-foreground transition-colors">Interview Prep</Link>
            </div>

            <div className="flex items-center space-x-3">
              {currentUser ? (
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-white" />
                    </div>
                    <span className="font-medium">{currentUser?.username || currentUser?.email}</span>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => navigate('/user-profile-dashboard')}
                  >
                    Dashboard
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => {
                      localStorage.removeItem('currentUser');
                      setCurrentUser(null);
                    }}
                  >
                    <LogOut className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => navigate('/user-login')}
                  >
                    Login
                  </Button>
                  <Button 
                    size="sm"
                    onClick={() => navigate('/user-registration')}
                  >
                    Get Started
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>
      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">
              Master Testing Through
              <span className="block bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                20 Comprehensive Roadmaps
              </span>
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              From manual testing fundamentals to advanced automation architectures. 
              Choose your path and become a testing expert with hands-on missions and real-world projects.
            </p>
            
            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600">20</div>
                <div className="text-sm text-muted-foreground">Learning Paths</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600">350+</div>
                <div className="text-sm text-muted-foreground">Missions</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600">8,200+</div>
                <div className="text-sm text-muted-foreground">Learners</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-orange-600">85%</div>
                <div className="text-sm text-muted-foreground">Success Rate</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Search and Filters */}
      <section className="py-8 px-4 sm:px-6 lg:px-8 border-b border-border">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            <div className="flex-1 max-w-2xl">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search roadmaps, technologies, or skills..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                  className="w-full pl-10 pr-4 py-3 border border-border rounded-lg bg-background focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center space-x-2"
              >
                <Filter className="w-4 h-4" />
                <span>Filters</span>
              </Button>
              
              {selectedRoadmaps?.length > 1 && (
                <Button
                  variant="secondary"
                  onClick={() => setShowComparison(true)}
                  className="flex items-center space-x-2"
                >
                  <ArrowRight className="w-4 h-4" />
                  <span>Compare ({selectedRoadmaps?.length})</span>
                </Button>
              )}
            </div>
          </div>
          
          {/* Filter Tags */}
          {(selectedFilters?.level !== 'all' || selectedFilters?.technology !== 'all' || selectedFilters?.industry !== 'all') && (
            <div className="flex flex-wrap gap-2 mt-4">
              {selectedFilters?.level !== 'all' && (
                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                  Level: {selectedFilters?.level}
                </span>
              )}
              {selectedFilters?.technology !== 'all' && (
                <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
                  Tech: {selectedFilters?.technology}
                </span>
              )}
              {selectedFilters?.industry !== 'all' && (
                <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                  Industry: {selectedFilters?.industry}
                </span>
              )}
            </div>
          )}
        </div>
      </section>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="space-y-6">
              {/* Recommendation Engine */}
              <RecommendationEngine currentUser={currentUser} roadmaps={roadmaps} />
              
              {/* Filter Sidebar */}
              {showFilters && (
                <FilterSidebar
                  selectedFilters={selectedFilters}
                  setSelectedFilters={setSelectedFilters}
                  onClose={() => setShowFilters(false)}
                />
              )}
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Featured Roadmaps */}
            {featuredRoadmaps?.length > 0 && !searchTerm && (
              <motion.div
                className="mb-12"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-2xl font-bold mb-6 flex items-center space-x-2">
                  <Star className="w-6 h-6 text-yellow-500" />
                  <span>Featured Roadmaps</span>
                </h2>
                <div className="grid md:grid-cols-2 gap-6">
                  {featuredRoadmaps?.slice(0, 4)?.map((roadmap) => (
                    <RoadmapCard
                      key={roadmap?.id}
                      roadmap={roadmap}
                      onSelect={() => handleRoadmapSelect(roadmap)}
                      isSelected={selectedRoadmaps?.some(r => r?.id === roadmap?.id)}
                      onEnroll={() => handleEnrollment(roadmap?.slug)}
                      featured={true}
                    />
                  ))}
                </div>
              </motion.div>
            )}

            {/* Trending Roadmaps */}
            {trendingRoadmaps?.length > 0 && !searchTerm && (
              <motion.div
                className="mb-12"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <h2 className="text-2xl font-bold mb-6 flex items-center space-x-2">
                  <TrendingUp className="w-6 h-6 text-green-500" />
                  <span>Trending Now</span>
                </h2>
                <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {trendingRoadmaps?.slice(0, 6)?.map((roadmap) => (
                    <RoadmapCard
                      key={roadmap?.id}
                      roadmap={roadmap}
                      onSelect={() => handleRoadmapSelect(roadmap)}
                      isSelected={selectedRoadmaps?.some(r => r?.id === roadmap?.id)}
                      onEnroll={() => handleEnrollment(roadmap?.slug)}
                    />
                  ))}
                </div>
              </motion.div>
            )}

            {/* All Roadmaps */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold">
                  {searchTerm ? `Search Results (${filteredRoadmaps?.length})` : 'All Roadmaps'}
                </h2>
                
                <div className="text-sm text-muted-foreground">
                  {filteredRoadmaps?.length} of {roadmaps?.length} roadmaps
                </div>
              </div>

              {filteredRoadmaps?.length > 0 ? (
                <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {filteredRoadmaps?.map((roadmap) => (
                    <RoadmapCard
                      key={roadmap?.id}
                      roadmap={roadmap}
                      onSelect={() => handleRoadmapSelect(roadmap)}
                      isSelected={selectedRoadmaps?.some(r => r?.id === roadmap?.id)}
                      onEnroll={() => handleEnrollment(roadmap?.slug)}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Search className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No roadmaps found</h3>
                  <p className="text-muted-foreground">
                    Try adjusting your search terms or filters to find what you're looking for.
                  </p>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>
      {/* Comparison Tool Modal */}
      {showComparison && (
        <ComparisonTool
          roadmaps={selectedRoadmaps}
          onClose={() => setShowComparison(false)}
          onEnroll={handleEnrollment}
        />
      )}
    </div>
  );
};

export default TestingRoadmapsHub;